import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import DocumentStatus from '@common/components/DocumentStatus/DocumentStatus';
import { StyledLink } from '@common/components/Link/styles';
import { IDocumentDto } from '@common/features/submission/types';
import { Id, DocumentType, DocumentStatus as DocumentStatusType} from '@common/types';
import { ISidebarSection } from '@common/components/Sidebar/types';
import { FeatureFlagsDto } from '@common/features/configuration/types';
import { formatDate, formatDateTime,  formatPeriodInDays } from '@common/helpers';
import { AssociatedContentReadonlySections } from '../../types';
import { IDocumentAttributes, getDocumentAttributes, getMediaContentType } from '@common/features/document/helpers';
import { DocumentContentCell } from '@common/features/document/components/DocumentContentCell';
import { htmlHeader } from '@common/constants';

interface IGetSections {
  showMissingAssociationWarning: boolean;
  showRetiredProductWarning: boolean;
  featureFlags: FeatureFlagsDto;
  disabledSections?: AssociatedContentReadonlySections;
}

export const getSections = ({
  showMissingAssociationWarning,
  showRetiredProductWarning,
  featureFlags: {
    showImagesOnAssociateContent,
    showLiveChat,
  },
  disabledSections,
}: IGetSections) => {
  const getCommonPilSmpcAttributes = (documentType: DocumentType) => ({
    showWarning: showMissingAssociationWarning,
    disable: disabledSections?.pilSmpcSelection,
    requiresRetirementReason: getDocumentAttributes(documentType, false).requiresRetirementReason,
  });

  const getCommonSecondaryDocumentsAttributes = (documentType: DocumentType) => ({
    showWarning: showRetiredProductWarning,
    disable: disabledSections?.secondaryDocumentsSelection,
    requiresRetirementReason: getDocumentAttributes(documentType, false).requiresRetirementReason,
  });

  const items: ISidebarSection<DocumentType>[] = [
    {
      name: 'SmPC',
      additionalInformation: DocumentType.Smpc,
      ...getCommonPilSmpcAttributes(DocumentType.Smpc),
    },
    {
      name: 'PIL',
      additionalInformation: DocumentType.Pil,
      ...getCommonPilSmpcAttributes(DocumentType.Pil),
    },
  ];

  items.push(
    {
      name: 'RMM',
      additionalInformation: DocumentType.Rmm,
      ...getCommonSecondaryDocumentsAttributes(DocumentType.Rmm),
    },
    {
      name: 'DHPC',
      additionalInformation: DocumentType.Dhcp,
      ...getCommonSecondaryDocumentsAttributes(DocumentType.Dhcp),
    },
    {
      name: 'User manual',
      additionalInformation: DocumentType.UserManual,
      ...getCommonSecondaryDocumentsAttributes(DocumentType.UserManual),
    },
    {
      name: 'Safety alert',
      additionalInformation: DocumentType.SafetyAlert,
      ...getCommonSecondaryDocumentsAttributes(DocumentType.SafetyAlert),
    },
    {
      name: 'Product information',
      additionalInformation: DocumentType.ProductInformation,
      ...getCommonSecondaryDocumentsAttributes(DocumentType.ProductInformation),
    },
    {
      name: 'Audio & video',
      additionalInformation: DocumentType.AudioVideo,
      ...getCommonSecondaryDocumentsAttributes(DocumentType.AudioVideo),
    },
  );

  if (showImagesOnAssociateContent) {
    items.push({ name: 'Images', additionalInformation: DocumentType.Images });
  }

  if (showLiveChat) {
    items.push({ name: 'Live chat', additionalInformation: DocumentType.LiveChat });
  }

  return items;
};

interface IPrepareColumns {
  documentAttributes: IDocumentAttributes;
  onFileOpen?: (documentId: Id) => void;
  onViewHtmlClick?: (documentId: Id, version: number, documentType: DocumentType, documentStatus: DocumentStatusType) => void;
}

export const prepareColumns = ({
  documentAttributes,
  onFileOpen,
  onViewHtmlClick = () => {},
}: IPrepareColumns) => {
  const columnHelper = createColumnHelper<IDocumentDto>();

  return [
    columnHelper.accessor('documentTitle', {
      header: 'Content title',
      cell: ({ getValue }) => getValue(),
    }),
    documentAttributes.showContentTypeColumn
      ? columnHelper.accessor('type', {
          header: 'Content type',
          cell: ({ getValue }) => getMediaContentType(getValue())
        })
      : undefined,
    documentAttributes.hasHtmlContent
      ? columnHelper.accessor(() => {}, {
          header: htmlHeader,
          cell: ({ row }) => (
            <StyledLink onClick={() => onViewHtmlClick(row.original.documentId, row.original.version, row.original.type, row.original.status)} to="#">
              View
            </StyledLink>
          ),
        })
      : columnHelper.accessor('originalFileName', {
          header:
            (documentAttributes.showRmmWebLink || documentAttributes.showAudioVideoWebLink) && !documentAttributes.allowFileUpload
              ? 'Weblink'
              : 'Content',
          cell: ({ getValue, row }) => {
            const isPublished = row.original.status === DocumentStatusType.Published;
            return (<DocumentContentCell fileName={getValue()} row={row} onFileOpen={isPublished ? undefined : onFileOpen} />)
          },
        }),
    columnHelper.accessor('version', {
      header: 'Version',
      cell: ({ getValue }) => getValue() ?? '-',
    }),
    documentAttributes.showHasDigitalVersion
      ? columnHelper.accessor('hasDigitalVersion', {
          header: 'ePIL',
          cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
        })
      : undefined,
    columnHelper.accessor('lastUpdateDate', {
      header: 'Date last updated',
      cell: ({ getValue }) => formatDate(getValue()),
    }),
    documentAttributes.showRegulatorApprovalDate
      ? columnHelper.accessor('regulatorApprovalDate', {
          header: 'MHRA Approval date',
          cell: ({ getValue }) => formatDate(getValue()),
        })
      : undefined,
    documentAttributes.showDaysSinceApproval
      ? columnHelper.accessor('daysSinceApproval', {
          header: 'Days since approval',
          id: 'daysSinceApproval',
          cell: ({ getValue }) => formatPeriodInDays(getValue()),
        })
      : undefined,
    documentAttributes.showHcpOnly
      ? columnHelper.accessor('isVisibleToHCP', {
          header: 'HCP only',
          cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
        })
      : undefined,
    columnHelper.accessor('submittedDate', {
      header: 'Date and Time Submitted',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? formatDateTime(new Date(value)) : '-';
      },
    }),
    columnHelper.accessor('linkedProductIds', {
      header: 'Associated Products',
      cell: ({ getValue }) => getValue()?.length ?? '-',
    }),
    columnHelper.accessor('documentId', {
      header: 'Content ID',
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: (info) => <DocumentStatus value={info.getValue()} conversionState={{
          isFailed: null,
          isInProgress: null
      }} />,
    }),
  ].filter((x) => x);
};
